import { Box, Card, Dialog } from "@mui/material";
import Grid from "@mui/material/Grid";
import ActorAvatar from "../ActorAvatar";
import DotMenu from "../DotMenu";
import DateRangeIcon from "@mui/icons-material/DateRange";
import AccountBoxIcon from "@mui/icons-material/AccountBox";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DownloadIcon from "@mui/icons-material/Download";
import { formatDateRange, formatDate } from "../../utils/dateTimeFormat";
import { TableAvatar } from "../../utils/dataGrid";
import { permitStatusToSortableValue, renderPermitStatus } from "./utils/permitStatus";
import BuildIcon from "@mui/icons-material/Build";
import PreviewIcon from "@mui/icons-material/Preview";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import Tooltip from "@mui/material/Tooltip";
import Translate from "../../utils/Translate";
import ConstructionIcon from "@mui/icons-material/Construction";
import Button from "@mui/material/Button";
import { RemoveRedEye } from "@mui/icons-material";
import PermitDetailsDialog from "./responsible-view/PermitDetailsDialog";
import { useState } from "react";
import { accessKeys, hasAccess } from "../../utils/userAccess";

export default function PermitCard(permit) {
  const {
    actorPermitId,
    actorId,
    dataId,
    actorName,
    actorEmail,
    actorPictureUrl,
    extraActions,
    machineGroup,
    validFrom,
    validTo,
    responsibleActorName,
    responsibleActorEmail,
    responsibleTitle,
    machineTypes,
    reviewDate,
    reviewIsDue,
    reviewer,
    showPermitDetailsButton,
  } = permit;

  const [showPermitDetails, setShowPermitDetails] = useState(false);

  const showDownloadButton =
    permit.blobFile === null ? !!permit.handleDownloadActorPermit : !!permit.downloadCustomPermit;
  if (showPermitDetailsButton && showDownloadButton) {
    throw new Error("Not supported"); // Can't show both buttons at the same time
  }
  if (showPermitDetailsButton && !hasAccess(accessKeys.actorPermitResponsibleView)) {
    throw new Error("Wrong access"); // Not implemented for anything else
  }
  if (showPermitDetailsButton && (!actorPermitId || !dataId)) {
    throw new Error("Missing actorPermitId or dataId");
  }

  return (
    <>
      {showPermitDetails && (
        <Dialog
          open
          onClose={() => setShowPermitDetails(false)}
          className="bigCourseDialog"
          maxWidth={false}
          scroll="paper"
        >
          <PermitDetailsDialog
            actorPermitId={actorPermitId}
            dataId={dataId}
            onClose={() => setShowPermitDetails(false)}
          />
        </Dialog>
      )}

      <Card sx={{ padding: "16px", marginBottom: "16px", transform: "translateZ(0)" }}>
        <ActorAvatar
          name={actorName}
          email={actorEmail}
          profilePictureUrl={actorPictureUrl}
          link={showPermitDetailsButton ? null : "/dashboard/person/" + actorId}
        />
        <Box sx={{ position: "fixed", top: "18px", right: "16px" }}>
          <DotMenu content={extraActions} horizontal />
        </Box>
        <Grid container spacing={4} direction="column" sx={{ padding: "30px 18px 16px 18px" }}>
          <Grid container alignItems="center">
            <ConstructionIcon />
            <Box marginLeft="19px">{machineGroup}</Box>
          </Grid>
          {!showPermitDetailsButton && (
            <Grid container alignItems="center">
              <BuildIcon />
              <Box marginLeft="19px">{machineTypes}</Box>
            </Grid>
          )}

          <Grid container alignItems="center">
            <DateRangeIcon />
            <Box marginLeft="19px">{formatDateRange(validFrom, validTo)}</Box>
          </Grid>

          {!showPermitDetailsButton && (
            <Grid container alignItems="center">
              <AccountBoxIcon />
              <Box marginLeft="19px">
                <TableAvatar
                  hideImage
                  boldTitle={false}
                  title={
                    !!responsibleActorName && !!responsibleTitle
                      ? `${responsibleActorName} / ${responsibleTitle}`
                      : (responsibleActorName ?? responsibleTitle ?? "—")
                  }
                  subtitle={[responsibleActorEmail ?? ""]}
                />
              </Box>
            </Grid>
          )}

          <Grid container alignItems="center">
            <Tooltip title={Translate.get("Review")}>
              <PreviewIcon />
            </Tooltip>
            <Box marginLeft="19px">
              {reviewer}{" "}
              {reviewIsDue ? (
                <Tooltip title={Translate.get("ReviewNeeded")}>
                  <PendingActionsIcon style={{ transform: "translateY(0.15em)" }} />
                </Tooltip>
              ) : (
                ""
              )}
              <br />
              {reviewDate != null ? formatDate(reviewDate) : ""}
            </Box>
          </Grid>

          <Grid container alignItems="center">
            <CheckCircleIcon />
            <Box marginLeft="19px" alignItems="center" display="flex">
              {renderPermitStatus(permitStatusToSortableValue(permit), permit)}
            </Box>
          </Grid>
          {(permit.blobFile === null ? !!permit.handleDownloadActorPermit : !!permit.downloadCustomPermit) && (
            <Grid container justifyContent="flex-end">
              <Button
                className="regularButtonWithIcon stdLightLoader"
                variant="outlined"
                sx={{ width: 80 }}
                loading={permit.actorPermitId === permit.loading}
                onClick={(event) => {
                  event.stopPropagation();
                  if (permit.blobFile === null) {
                    permit.handleDownloadActorPermit(permit.actorPermitId, permit.actorName ?? actorName);
                  } else {
                    permit.downloadCustomPermit(permit.blobFile.blobDocumentId);
                  }
                }}
              >
                <DownloadIcon />
              </Button>
            </Grid>
          )}
          {showPermitDetailsButton && (
            <Grid container alignItems="center">
              <Button
                onClick={() => setShowPermitDetails(true)}
                size="large"
                variant="contained"
                fullWidth={true}
                sx={{ marginTop: "6px" }}
                startIcon={<RemoveRedEye />}
              >
                {Translate.get("Show")}
              </Button>
            </Grid>
          )}
        </Grid>
      </Card>
    </>
  );
}
